import {
  Typography,
  Box,
  IconButton,
  Collapse,
  useTheme,
  Button
} from '@material-ui/core'
import {
  KeyboardArrowDown,
  KeyboardArrowRight,
  ExpandMoreOutlined,
  ExpandLessOutlined,
  KeyboardArrowLeft
} from '@material-ui/icons'
import React, { useEffect, useState } from 'react'
import ReactJson from 'react-json-view'

import { getTranslate, lang } from '../../../localization'
import useStyles from './styles/JsonViewer.style'

interface JsonViewerProps {
  jsonData: unknown
  openForResult?: boolean
}

interface JsonNodeProps {
  data: unknown
  level: number
  openAll: boolean
  openForResult: boolean
}

const JsonEntry = ({
  name,
  children,
  level,
  openAll,
  openForResult
}: Omit<JsonNodeProps, 'data'> & {
  name: string
  children: React.ReactNode
}) => {
  const classes = useStyles()
  const theme = useTheme()
  const [open, setOpen] = useState(openForResult)
  useEffect(() => setOpen(openAll || openForResult), [openAll, openForResult])
  return (
    <Box
      mb={level / 4}
      ml={level / 2}
      borderLeft={
        level !== 0 ? `1px solid ${theme.palette.grey[700]}` : undefined
      }>
      <Box display="flex" alignItems="center" ml={level / 2}>
        <IconButton
          size="small"
          aria-label={`${getTranslate(open ? 'Collapse' : 'Expand')} ${name}`}
          aria-expanded={open}
          onClick={() => setOpen(!open)}>
          {open ? (
            <KeyboardArrowDown />
          ) : lang === 'fa' ? (
            <KeyboardArrowLeft />
          ) : (
            <KeyboardArrowRight />
          )}
        </IconButton>
        <Typography variant="subtitle1">{name}: </Typography>
      </Box>
      <Collapse in={open}>{children}</Collapse>
      <span className={classes.horizontalLineForValue} />
    </Box>
  )
}

const JsonNode = ({ data, level, openAll, openForResult }: JsonNodeProps) => {
  const classes = useStyles()
  const theme = useTheme()
  if (Array.isArray(data)) {
    return (
      <Box>
        <Box ml={level / 2} mb={level / 4}>
          {data.map((item, index) => (
            <Box
              key={index}
              borderLeft={`1px solid ${theme.palette.grey[700]}`}>
              <JsonNode {...{ level, openAll, openForResult }} data={item} />
            </Box>
          ))}
        </Box>
        <span className={classes.horizontalLineForValue} />
      </Box>
    )
  }
  if (typeof data === 'object' && data !== null) {
    return (
      <>
        {Object.entries(data).map(([name, value]) => (
          <JsonEntry key={name} {...{ name, level, openAll, openForResult }}>
            <JsonNode
              data={value}
              level={level + 4}
              {...{ openAll, openForResult }}
            />
          </JsonEntry>
        ))}
      </>
    )
  }
  let nested: object | null = null
  if (typeof data === 'string') {
    try {
      const parsed: unknown = JSON.parse(data)
      if (typeof parsed === 'object' && parsed !== null) nested = parsed
    } catch {
      // Ordinary strings are displayed as text, not parsed JSON.
    }
  }
  return (
    <div className={classes.valueSection}>
      <Box ml={level / 2}>
        <Typography variant="subtitle1" className={classes.dataContainer}>
          {nested ? (
            <ReactJson src={nested} displayObjectSize />
          ) : data === null || data === undefined ? (
            '—'
          ) : (
            String(data)
          )}
        </Typography>
      </Box>
    </div>
  )
}

const hasDisclosure = (data: unknown): boolean =>
  Array.isArray(data)
    ? data.some(hasDisclosure)
    : data !== null && typeof data === 'object' && Object.keys(data).length > 0

const JsonViewer = ({ jsonData, openForResult = false }: JsonViewerProps) => {
  const classes = useStyles()
  const [openAll, setOpenAll] = useState(false)
  return (
    <div className={classes.root} style={{ direction: 'ltr' }}>
      <Box>
        <JsonNode data={jsonData} level={0} {...{ openAll, openForResult }} />
      </Box>
      {!openForResult && hasDisclosure(jsonData) && (
        <div className={classes.buttonContainer}>
          <Button
            onClick={() => setOpenAll(!openAll)}
            variant={openAll ? 'outlined' : 'contained'}
            color="primary"
            endIcon={openAll ? <ExpandLessOutlined /> : <ExpandMoreOutlined />}>
            {openAll ? getTranslate('Close All') : getTranslate('Open All')}
          </Button>
        </div>
      )}
    </div>
  )
}

export default JsonViewer
